import { appEventEmitter } from "@shared/events/app-event-emitter";
import { useCallback, useEffect, useRef } from "react";
import { useGuidedTourLogic } from "./useGuidedTourLogic";

const START_DELAY_MS = 2000;

export const useGuidedToursEventTracking = () => {
  const {
    isOnboardingTourPending,
    isOnboardingTourRunning,
    startTour,
    completeTour,
  } = useGuidedTourLogic();

  const pendingStartRef = useRef<ReturnType<typeof setTimeout>>();
  // The welcome flow's own scripted auto-submit fires the same
  // request/response events a real user message does. These flags let us
  // recognize that specific exchange - the request, then its response -
  // instead of mistaking it for the user sending a message and cancelling
  // the tour, and let us wait until the reply actually finishes streaming
  // in before starting the delay, not just until it was sent.
  const expectingScriptedRequestRef = useRef(false);
  const expectingScriptedResponseRef = useRef(false);

  const listener = useCallback(
    (_: unknown, eventType: string) => {
      if (eventType === "sidekick-welcome-flow-complete") {
        expectingScriptedRequestRef.current = true;
        return;
      }

      if (eventType === "sidekick-prompt-request-success") {
        if (expectingScriptedRequestRef.current) {
          expectingScriptedRequestRef.current = false;
          expectingScriptedResponseRef.current = true;
          return;
        }

        if (isOnboardingTourRunning || pendingStartRef.current) {
          clearTimeout(pendingStartRef.current);
          pendingStartRef.current = undefined;
          completeTour();
        }
        return;
      }

      if (eventType === "sidekick-prompt-response-failure") {
        if (expectingScriptedResponseRef.current) {
          expectingScriptedResponseRef.current = false;
          completeTour();
        }
        return;
      }

      if (eventType !== "sidekick-prompt-response-success") return;

      if (expectingScriptedResponseRef.current) {
        expectingScriptedResponseRef.current = false;
        if (isOnboardingTourPending) {
          pendingStartRef.current = setTimeout(startTour, START_DELAY_MS);
        }
      }
    },
    [isOnboardingTourPending, isOnboardingTourRunning, startTour, completeTour],
  );

  useEffect(() => {
    appEventEmitter.addGlobalListener(listener);
    return () => {
      appEventEmitter.removeGlobalListener(listener);
      clearTimeout(pendingStartRef.current);
    };
  }, [listener]);
};
